import { truncate } from "lodash-es";
import moment from "moment-timezone";
import { useState } from "react";
import LinkButton from "src/components/buttons/LinkButton";
import OfferingModel from "src/models/OfferingModel";
import { useStore } from "src/stores/Store";
import formatMoney from "src/utils/formatMoney";
import { AnnualFeeType } from "types/code-generator";

interface Props {
  offering: OfferingModel;
  isDetail?: boolean;
}

interface State {
  viewingProgramDescription: boolean;
  viewingOfferingDescription: boolean;
}

export function OfferingDetail(props: Props) {
  const store = useStore();
  const [state, setState] = useState<State>({
    viewingProgramDescription: false,
    viewingOfferingDescription: false,
  });
  const today = moment();

  function viewProgramDescription() {
    setState({
      ...state,
      viewingProgramDescription: !state.viewingProgramDescription,
    });
  }

  function renderEligibility() {
    if (props.offering.allowNonMembers) {
      return null;
    }

    return (
      <div className="mb-4">
        <h3 className="step-subhead">Eligibility</h3>
        <div className="mb-2 text-sm text-empty">
          This {props.offering.type.toLowerCase()} is available to accounts with
          the following memberships.{" "}
          {!store.user && (
            <>
              If you are a member,{" "}
              <LinkButton to={`/login?returnPathname=${location.pathname}`}>
                sign in
              </LinkButton>{" "}
              to register.
            </>
          )}
        </div>
        <div className="divide-y divide-borderGray">
          {[...props.offering.membershipOfferings].map((m) => {
            const published = m.publishDate && moment(m.publishDate);

            if (!published || today.isBefore(published)) {
              return (
                <div key={m.id}>
                  <div
                    css={{
                      color: store.theme.orgColor,
                    }}
                    className="flex items-center py-2"
                  >
                    <i
                      className="far fa-certificate mr-1"
                      css={{
                        color: store.theme.orgColor,
                      }}
                    />
                    <div className="flex-1">
                      {m.program && m.program.title}{" "}
                      {m.program && m.title && ` • `} {m.title}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={m.id}>
                <LinkButton
                  className="flex py-2 text-organization"
                  to={`/find/${m.id}`}
                >
                  <i className="far fa-certificate mr-1 mt-1 text-organization" />
                  <div className="flex-1">
                    {m.program && m.program.title}{" "}
                    {m.program && m.title && ` • `} {m.title}
                  </div>
                </LinkButton>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  let programDescription;
  let annualFee;
  const lessonPricing = [];
  const eligibility = renderEligibility();

  if (
    props.offering.program &&
    props.offering.program.description &&
    props.offering.program.description.length > 100 &&
    !state.viewingProgramDescription &&
    !props.isDetail
  ) {
    programDescription = (
      <p>
        {truncate(props.offering.program.description, {
          length: 100,
          omission: "…",
        })}{" "}
        <LinkButton onClick={viewProgramDescription}>expand</LinkButton>
      </p>
    );
  } else if (props.offering.program && props.offering.program.description) {
    programDescription = (
      <div>
        <div
          dangerouslySetInnerHTML={{
            __html: props.offering.program.description,
          }}
        />
        {props.offering.program.description.length > 100 && !props.isDetail ? (
          <LinkButton onClick={viewProgramDescription}>collapse</LinkButton>
        ) : null}
      </div>
    );
  }

  if (
    props.offering.department &&
    props.offering.applyAnnualFee &&
    props.offering.department.annualFeeAmount > 0
  ) {
    annualFee = (
      <>
        <h3 className="step-subhead">
          Annual Fee for {store.organization.title}
        </h3>
        <div className="mb-4">
          <strong>
            ${formatMoney(props.offering.department.annualFeeAmount)}
          </strong>{" "}
          {props.offering.department &&
            props.offering.department.annualFeeBillingCategory &&
            props.offering.department.annualFeeBillingCategory.title}{" "}
          <span className="text-sm text-empty">
            {props.offering.department &&
            props.offering.department.annualFeeType === AnnualFeeType.Account
              ? "per family"
              : "per student"}
          </span>
        </div>
      </>
    );
  }

  if (props.offering.price) {
    lessonPricing.push(
      <div key="price">
        <strong>${formatMoney(props.offering.price)}</strong> per session when 1{" "}
        {props.offering.participantType.toLowerCase()} is enrolled in a
        registration.
      </div>
    );
  }

  if (props.offering.allowExtension && props.offering.extensionPrice > 0) {
    lessonPricing.push(
      <div key="extensionPrice">
        <strong>${formatMoney(props.offering.extensionPrice)}</strong>{" "}
        extensions
      </div>
    );
  }

  // if (offering.priceForAdditionalStudents > 0) {
  //   lessonPricing.push(
  //     <div key="price-multiple">
  //       <strong>${formatMoney(offering.priceForAdditionalStudents)}</strong>{" "}
  //       per student, per lesson, when multiple students are enrolled in the
  //       same series.
  //     </div>
  //   );
  // }

  if (props.offering.priceAdjustments) {
    for (const pa of props.offering.priceAdjustments) {
      lessonPricing.push(
        <div key={pa.id}>
          <strong>${formatMoney(pa.price)}</strong> per session when {pa.min}{" "}
          {props.offering.participantType.toLowerCase()}s are enrolled in a
          registration.
        </div>
      );

      const extensionPriceAdjustment =
        props.offering.extensionPriceAdjustments.find(
          (epa) => epa.min === pa.min
        );

      if (extensionPriceAdjustment) {
        lessonPricing.push(
          <div key={extensionPriceAdjustment.id}>
            <strong>${formatMoney(extensionPriceAdjustment.price)}</strong>{" "}
            extensions
          </div>
        );
      }
    }
  }

  return (
    <>
      {props.isDetail && <hr className="mt-8" />}
      <div className="pt-4">
        {programDescription && (
          <>
            <h3 className="step-subhead">Program Description</h3>
            <div className="mb-4">{programDescription}</div>
          </>
        )}

        {props.offering.description && (
          <>
            <h3 className="step-subhead">{props.offering.type} Description</h3>
            <div
              className="mb-4"
              dangerouslySetInnerHTML={{ __html: props.offering.description }}
            />
          </>
        )}

        {lessonPricing.length > 0 && (
          <>
            <h3 className="step-subhead">Pricing</h3>
            <div className="mb-4">{lessonPricing}</div>
            {annualFee}
          </>
        )}

        {eligibility}
      </div>
    </>
  );
}
